import { useState, useCallback, useRef } from 'react';
import { formatMsg, getProgressBarFrame, SHORTCUTS, TERMINAL_SESSION_ID } from './terminalUtils';
import restApi from '../../services/restApi';

const PROMPT = '> ';

export const useTerminalLogic = () => {
  const [inputBuffer, setInputBuffer] = useState('');
  const [cursorPos, setCursorPos] = useState(0);
  const [history, setHistory] = useState([]);
  const [historyIndex, setHistoryIndex] = useState(-1);
  const [isLoading, setIsLoading] = useState(false);

  // 弹窗状态
  const [isModalOpen, setIsModalOpen] = useState(false);
  const loadingInterval = useRef(null);

  // 插入字符逻辑 (支持中间插入)
  const handleInsert = useCallback(
    (char, term) => {
      const prefix = inputBuffer.slice(0, cursorPos);
      const suffix = inputBuffer.slice(cursorPos);
      const newText = char.toUpperCase();

      setInputBuffer(prefix + newText + suffix);
      setCursorPos(cursorPos + newText.length);

      term.write(newText + suffix);
      for (let i = 0; i < suffix.length; i++) term.write('\b');
    },
    [inputBuffer, cursorPos],
  );

  // 退格逻辑
  const handleBackspace = useCallback(
    (term) => {
      if (cursorPos <= 0) return;
      const prefix = inputBuffer.slice(0, cursorPos);
      const suffix = inputBuffer.slice(cursorPos);

      setInputBuffer(prefix.slice(0, -1) + suffix);
      setCursorPos(cursorPos - 1);

      term.write(`\b${suffix} `);
      for (let i = 0; i <= suffix.length; i++) term.write('\b');
    },
    [inputBuffer, cursorPos],
  );

  // 执行命令
  const executeCommand = async (term, commandText) => {
    const command = commandText || inputBuffer.trim();
    if (!command) {
      term.write(`\r\n${PROMPT}`);
      return;
    }

    term.write('\r\n');
    setIsLoading(true);
    let tick = 0;
    loadingInterval.current = setInterval(() => {
      term.write(`\r\x1b[K${getProgressBarFrame(tick)}`);
      tick++;
    }, 200);

    try {
      // 模拟 HTTP 请求
      const sessionId = sessionStorage.getItem(TERMINAL_SESSION_ID) || 'NO_SESSION';
      const res = await restApi.post('/flow/api/flow-rest/terminal/command/run', {
        command,
        sessionId,
      });

      clearInterval(loadingInterval.current);
      term.write('\r\x1b[K'); // 清除进度条

      const upperRes = formatMsg(res.result);

      // 指令拦截
      if (upperRes.startsWith('START_SERVICE_WINDOW')) {
        const parts = res.result.split(' ');
        if (parts.length > 1) {
          const path = parts[1];
          const features = parts.length >= 3 ? parts[2] : 'width=600,height=400,left=400,top=400';
          window.open(`/ui/${path}`, 'ChildWindow', features);
        } else {
          const child = window.open('', '_blank', 'width=600,height=400,left=200,top=200');
          child?.document.write(
            "<h3>CHILD WINDOW</h3><button onclick=\"window.opener.postMessage('HELLO FROM CHILD', '*')\">SEND TO PARENT</button>",
          );
        }
        term.write('SYSTEM: WINDOW OPENED\r\n');
      } else if (upperRes === 'START_SERVICE_DIALOG') {
        setIsModalOpen(true);
        term.write('SYSTEM: DIALOG OPENED\r\n');
      } else {
        term.write(`${upperRes}\r\n`);
      }

      setHistory((prev) => [...prev, command.toUpperCase()]);
    } finally {
      setIsLoading(false);
      setInputBuffer('');
      setCursorPos(0);
      setHistoryIndex(-1);
      term.write(PROMPT);
    }
  };

  const printHelp = (term) => {
    term.write('\r\n\x1b[1;32m=== AVAILABLE SHORTCUTS ===\x1b[0m\r\n');
    SHORTCUTS.forEach((s) => {
      // 使用 ANSI 转义序列加粗 key 部分
      term.write(`\x1b[33m${s.key.padEnd(15)}\x1b[0m : ${s.desc}\r\n`);
    });
    term.write(`\r\n${PROMPT}${inputBuffer}`);
  };

  return {
    inputBuffer,
    setInputBuffer,
    cursorPos,
    setCursorPos,
    history,
    historyIndex,
    setHistoryIndex,
    isLoading,
    isModalOpen,
    setIsModalOpen,
    handleInsert,
    handleBackspace,
    executeCommand,
    printHelp,
    PROMPT,
  };
};
